/* eslint-disable eqeqeq */
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import Accordian from "../accordian";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import TextInput from "../inputs/TextInput";
import debounce from "lodash.debounce";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import { useGetAllExternalUsersReport } from "../../hooks/report/query";
import { EXPORT_PAGE_SIZE } from "../../constants/common";
import { Button } from "../button";
import { ExportExternalUsersReportForExcelPDFBody } from "../../hooks/report/interface";

interface SearchExternalUsersReportCardProps {
  setAllExternalUsers: Dispatch<SetStateAction<never[]>>;
  page: number;
  setPages: Dispatch<SetStateAction<number>>;
  search: string;
  setCount: Dispatch<SetStateAction<number>>;
  setExportBody: Dispatch<
    SetStateAction<ExportExternalUsersReportForExcelPDFBody | undefined>
  >;
  pageSize: number;
  setPage: Dispatch<SetStateAction<number>>;
}

const SearchExternalUsersReportCardSchema = Yup.object({
  fullName: Yup.string(),
  companyName: Yup.string(),
});

const initialState = {
  fullName: "",
  companyName: "",
};

const SearchExternalUsersReportCard = ({
  setAllExternalUsers,
  page,
  setPages,
  setCount,
  setExportBody,
  pageSize,
  setPage,
}: SearchExternalUsersReportCardProps) => {
  const [selectedKey, setSelectedKey] = useState<string>("");

  const {
    control,
    watch,
    formState: { errors },
    reset,
  } = useFormWithSchema(SearchExternalUsersReportCardSchema);

  const { t } = useTranslation();

  const fullName = watch("fullName");
  const companyName = watch("companyName");

  const [debouncedFullName, setDebouncedFullName] = useState(fullName);
  const [debouncedCompanyName, setDebouncedCompanyName] = useState(companyName);

  const debouncedFullNameSetter = useMemo(
    () => debounce((value: string) => setDebouncedFullName(value), 500),
    []
  );

  const debouncedCompanyNameSetter = useMemo(
    () => debounce((value: string) => setDebouncedCompanyName(value), 500),
    []
  );

  useEffect(() => {
    debouncedFullNameSetter(fullName || "");
    return () => debouncedFullNameSetter.cancel();
  }, [fullName, debouncedFullNameSetter]);

  useEffect(() => {
    debouncedCompanyNameSetter(companyName || "");
    return () => debouncedCompanyNameSetter.cancel();
  }, [companyName, debouncedCompanyNameSetter]);

  const { data: externalUsers } = useGetAllExternalUsersReport({
    search: debouncedFullName,
    companyName: debouncedCompanyName,
    pageNumber: page,
    pageSize: pageSize,
  });

  useEffect(() => {
    setExportBody({
      search: debouncedFullName,
      companyName: debouncedCompanyName,
      pageNumber: page,
      pageSize: EXPORT_PAGE_SIZE,
    });
  }, [debouncedCompanyName, debouncedFullName, page, setExportBody]);

  useEffect(() => {
    if (externalUsers) {
      setAllExternalUsers(externalUsers.data || []);
      setPages(externalUsers.pagination?.pages || 1);
      setCount(externalUsers.pagination?.count || 0);
    }
  }, [setAllExternalUsers, setPages, setCount, externalUsers]);

  useEffect(() => {
    setPage(1);
  }, [fullName, companyName, setPage]);

  const onClear = useCallback(() => {
    reset(initialState);
  }, [reset]);

  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordian
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 -mt-2">
            {t("searchFilters")}
          </h1>
        }
      >
        <div className="flex flex-col gap-4">
          <form className="flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 lg:gap-4">
              <Controller
                name="fullName"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <TextInput
                    label={t("fullName")}
                    onChange={(e) => {
                      onChange(e);
                      debouncedFullNameSetter(e.target.value);
                    }}
                    error={errors?.[name]?.message}
                    value={value}
                  />
                )}
              />
              <Controller
                name="companyName"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <TextInput
                    label={t("companyName")}
                    onChange={(e) => {
                      onChange(e);
                      debouncedCompanyNameSetter(e.target.value);
                    }}
                    error={errors?.[name]?.message}
                    value={value}
                  />
                )}
              />
            </div>
          </form>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={onClear}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("clear")}
            </Button>
          </div>
        </div>
      </Accordian>
    </div>
  );
};

export default React.memo(SearchExternalUsersReportCard);
